import { Box, Paper, Typography, Button } from "@mui/material";
import { useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import PaymentsIcon from "@mui/icons-material/Payments";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { colors, alphaVar } from "../../../../ui/colors";
import { PaymentGateway } from "../actions/getAvailablePaymentGateways.actions";

interface PaymentMethodPreviewProps {
  selectedGateways: PaymentGateway[];
  totalAmount?: number;
  showPreviewWrapper?: boolean;
  onPaymentClick?: (selectedGatewayId: string) => void;
  buttonDisabled?: boolean;
}

export const PaymentMethodPreview = ({
  selectedGateways,
  totalAmount = 3726.0,
  showPreviewWrapper = true,
  onPaymentClick,
  buttonDisabled = false,
}: PaymentMethodPreviewProps) => {
  const [previewSelectedId, setPreviewSelectedId] = useState<string>(
    selectedGateways[0]?.id || ""
  );
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const getGatewayIcon = (iconName: string) => {
    switch (iconName) {
      case "payments":
        return <PaymentsIcon sx={{ fontSize: 18, color: "white" }} />;
      case "account_balance_wallet":
        return <AccountBalanceWalletIcon sx={{ fontSize: 18, color: "white" }} />;
      default:
        return <PaymentsIcon sx={{ fontSize: 18, color: "white" }} />;
    }
  };

  const getGatewayDisplayName = (gateway: PaymentGateway) => {
    return gateway.displayName;
  };

  const handlePayment = () => {
    if (onPaymentClick && previewSelectedId) {
      onPaymentClick(previewSelectedId);
    }
  };

  if (selectedGateways.length === 0) {
    return null;
  }

  const paymentMethodsContent = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {selectedGateways.map((gateway) => {
        const isSelected = previewSelectedId === gateway.id;
        return (
          <Box
            key={gateway.id}
            onClick={() => setPreviewSelectedId(gateway.id)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 1.5,
              bgcolor: isSelected
                ? alphaVar(colors.orange.base, 0.05)
                : "background.paper",
              border: `1px solid ${isSelected
                  ? colors.orange.base
                  : alphaVar(colors.gray[200], 0.5)
                }`,
              borderRadius: 2,
              cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": {
                borderColor: colors.orange.base,
                bgcolor: alphaVar(colors.orange.base, 0.04),
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: gateway.iconColor,
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {getGatewayIcon(gateway.iconName)}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 700,
                  color: colors.gray[600],
                }}
              >
                {getGatewayDisplayName(gateway)}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                bgcolor: isSelected ? colors.orange.base : "transparent",
                border: `2px solid ${isSelected ? colors.orange.base : colors.gray[300]
                  }`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isSelected && (
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: "var(--fx-surface-base, #FFFFFF)",
                  }}
                />
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  const payButton = (
    <Button
      fullWidth
      variant="contained"
      disabled={buttonDisabled}
      onClick={handlePayment}
      sx={{
        mt: 1,
        py: 1.5,
        bgcolor: colors.orange.base,
        color: "white",
        fontWeight: 700,
        fontSize: "0.875rem",
        borderRadius: 2,
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        "&:hover": {
          bgcolor: colors.orange.dark,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        },
        "&.Mui-disabled": {
          bgcolor: colors.orange.base,
          color: "white",
          opacity: 0.6,
        },
      }}
    >
      Pay {formatCurrency(totalAmount)}
    </Button>
  );

  if (showPreviewWrapper) {
    return (
      <Box
        sx={{
          mt: 3,
          pt: 3,
          borderTop: `1px solid ${alphaVar(colors.gray[200], 0.3)}`,
        }}
      >
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            color: colors.gray[400],
            mb: 2,
            display: "block",
          }}
        >
          Customer Preview
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            bgcolor: alphaVar(colors.gray[50], 0.3),
            borderStyle: "dashed",
            borderColor: alphaVar(colors.gray[300], 0.5),
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Divider with text */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  height: "1px",
                  flex: 1,
                  bgcolor: alphaVar(colors.gray[200], 0.5),
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.625rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: colors.gray[400],
                }}
              >
                Hosted Payment Page
              </Typography>
              <Box
                sx={{
                  height: "1px",
                  flex: 1,
                  bgcolor: alphaVar(colors.gray[200], 0.5),
                }}
              />
            </Box>

            {/* Payment method selection */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: colors.gray[600],
                }}
              >
                Select payment method
              </Typography>
              {paymentMethodsContent}
            </Box>

            {payButton}
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 400 }}>
      <Typography
        variant="body2"
        fontWeight={700}
        sx={{ mb: 2, color: colors.gray[600] }}
      >
        Select payment method
      </Typography>
      {paymentMethodsContent}
      {payButton}
    </Box>
  );
};
